import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import { Wallet, ChevronDown, ChevronLeft, ChevronRight, Download } from 'lucide-react';
// import { getBalance } from '../utils/balance';
import { getSellerStats } from '../utils/sellerStats';
import { Wallet, ChevronDown, ChevronLeft, ChevronRight, Download, ExternalLink, RefreshCw } from 'lucide-react';
import { getBalance, clearBalanceCache } from '../utils/balance';
import { useCurrency } from '../contexts/CurrencyContext';
import { useTranslation } from '../hooks/useTranslation';
import { getCachedUser } from '../utils/auth';
import { Skeleton } from './common/Skeleton';
import WithdrawalModal from './WithdrawalModal';
import '../styles/WalletTab.css';

const WalletTab = () => {
  const navigate = useNavigate();
  const { currentCurrency, formatCurrency: formatCurrencyAmount, getCurrencySymbol } = useCurrency();
  const { t } = useTranslation();
  const currentUser = getCachedUser();

  // Load from cache immediately for instant display
  const getCachedBalance = () => {
    if (typeof localStorage !== 'undefined') {
      try {
        const cached = localStorage.getItem('cached_balance');
        if (cached) {
          return JSON.parse(cached);
        }
      } catch (e) {
        console.error('Failed to parse cached balance:', e);
      }
    }
    return null;
  };

  const cachedBalance = getCachedBalance();
  const [balance, setBalance] = useState(cachedBalance);
  const [transactions, setTransactions] = useState(cachedBalance?.transactions || []);
  const [withdrawals, setWithdrawals] = useState(cachedBalance?.withdrawals || []);
  const [disputeTransactions, setDisputeTransactions] = useState(cachedBalance?.dispute_transactions || []);
  const [purchaseTransactions, setPurchaseTransactions] = useState(cachedBalance?.purchase_transactions || []);
  const [loading, setLoading] = useState(!cachedBalance);
  const [stats, setStats] = useState({
    totalSales: 0,
    salesThisMonth: 0,
    salesThisMonthChange: 0,
    totalEarned: 0,
    totalEarnedChange: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [isSeller, setIsSeller] = useState(!!currentUser?.is_seller);
  const [activeTab, setActiveTab] = useState(isSeller ? 'withdrawals' : 'purchases'); // Default tab based on user type
  const [dateFilter, setDateFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    // Load fresh data in background (will use cache if available)
    loadWalletData(true);
    
    // Check if user is seller and load stats
    const user = getCachedUser();
    if (user?.is_seller) {
      setIsSeller(true);
      loadStats();
    }
  }, []);

  // Auto-refresh processing withdrawals
  useEffect(() => {
    // Check if there are any processing withdrawals from withdrawals table
    const hasProcessingWithdrawals = withdrawals.some(
      w => w.status === 'processing' || w.status === 'pending'
    ) || transactions.some(
      t => t.reference_type === 'withdrawal' && (t.status === 'processing' || t.status === 'pending')
    );
    
    if (!hasProcessingWithdrawals) {
      return; // No processing withdrawals, no need to poll
    }
    
    console.log('[WalletTab] Found processing withdrawals, starting auto-refresh...');
    
    // Refresh immediately, then every 10 seconds
    const refreshInterval = setInterval(() => {
      console.log('[WalletTab] Auto-refreshing to check withdrawal status...');
      // Clear cache and fetch fresh data
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('cached_balance');
      }
      loadWalletData(false); // Force fresh data, no cache
    }, 10000); // Refresh every 10 seconds
    
    // Also refresh when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[WalletTab] Page visible, refreshing withdrawal status...');
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('cached_balance');
        }
        loadWalletData(false);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [transactions, withdrawals]);

  const loadWalletData = async (useCache = true) => {
    // If we have cached data, show it immediately and fetch fresh in background
    if (useCache && balance) {
      // Fetch fresh data in background
      loadWalletData(false);
      return;
    }
    
    try {
      if (!useCache) {
        setLoading(true);
      }
      const balanceData = await getBalance();
      if (balanceData) {
        setBalance(balanceData);
        // Cache the balance data
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('cached_balance', JSON.stringify(balanceData));
        }
        // Transactions should come from balance API
        if (balanceData.transactions) {
          setTransactions(balanceData.transactions);
        }
        // Withdrawals from withdrawals table
        if (balanceData.withdrawals) {
          setWithdrawals(balanceData.withdrawals);
        }
        // Dispute transactions
        if (balanceData.dispute_transactions) {
          setDisputeTransactions(balanceData.dispute_transactions);
        }
        // Purchase transactions
        if (balanceData.purchase_transactions) {
          setPurchaseTransactions(balanceData.purchase_transactions);
        }
      }
    } catch (error) {
      console.error('Failed to load wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  // Use CurrencyContext for formatting - always use user's selected currency
  const formatCurrency = (amount, originalCurrency = null) => {
    // Always format using user's selected currency, regardless of original currency
    return formatCurrencyAmount(Math.abs(parseFloat(amount || 0)), currentCurrency);
  };

  const getTransactionDescription = (transaction) => {
    // Check if it's a dispute transaction (from dispute_info or metadata)
    if (transaction.dispute_info || transaction.metadata?.dispute_id) {
      const disputeInfo = transaction.dispute_info || {};
      const action = disputeInfo.resolution_action || transaction.metadata?.action;
      
      if (action === 'refund_buyer') {
        return `Dispute Refund: ${disputeInfo.reason || transaction.description || 'Order refunded to buyer'}`;
      } else if (action === 'pay_seller' || transaction.is_dispute_loss) {
        return `Dispute Resolved (Seller Paid): ${disputeInfo.reason || 'Funds released to seller'}`;
      } else if (transaction.is_dispute_pending) {
        return `Dispute Pending: ${disputeInfo.reason || 'Dispute under review'}`;
      }
      return `Dispute: ${disputeInfo.reason || transaction.description || 'Dispute transaction'}`;
    }
    
    if (transaction.type === 'debit' && transaction.reference_type === 'order') {
      return t('wallet.purchasedOrder');
    } else if (transaction.type === 'credit' && (transaction.reference_type === 'payment' || transaction.description?.toLowerCase().includes('top up'))) {
      return t('wallet.balanceTopUp');
    } else if (transaction.type === 'withdrawal' || transaction.isWithdrawal) {
      const method = transaction.metadata?.payment_method;
      if (method) {
        const pretty = method === 'paypal' ? 'PayPal' : method === 'stripe' ? 'Stripe' : method === 'coingate' ? 'CoinGate' : method;
        return `Withdrawal (${pretty})`;
      }
      return t('wallet.withdrawal');
    } else if (transaction.type === 'refund') {
      return transaction.description || t('wallet.refund');
    } else if (transaction.type === 'sale') {
      return t('wallet.sale');
    }
    return transaction.description || t('common.transaction', 'Transaction');
  };
  
  const getOrderId = (transaction) => {
    // For withdrawals, use the withdrawal ID
    if (transaction.reference_type === 'withdrawal') {
      return transaction.reference_id || transaction.id;
    }
    if (transaction.reference_id && transaction.reference_type === 'order') {
      return transaction.reference_id;
    }
    return transaction.id;
  };
  
  const hasReceipt = (transaction) => {
    return transaction.type === 'credit' && (transaction.reference_type === 'payment' || transaction.description?.toLowerCase().includes('top up'));
  };

  // Get transactions for current tab
  const getCurrentTabTransactions = () => {
    if (activeTab === 'withdrawals') {
      // Convert withdrawals to transaction-like format
      return withdrawals.map(w => ({
        id: w.id,
        user_id: w.user_id,
        amount: -Math.abs(parseFloat(w.amount || 0)), // Negative for withdrawal
        currency: w.currency || 'USD',
        type: 'withdrawal',
        description: `Withdrawal (${w.payment_method || 'Unknown'})`,
        reference_type: 'withdrawal',
        reference_id: w.id,
        status: w.status || 'pending',
        metadata: {
          ...w.metadata,
          payment_method: w.payment_method,
          withdrawal_id: w.id,
          stripe_payout_id: w.stripe_payout_id,
        },
        created_at: w.created_at,
        updated_at: w.updated_at,
        isWithdrawal: true,
      }));
    } else if (activeTab === 'disputes') {
      return disputeTransactions;
    } else if (activeTab === 'purchases') {
      return purchaseTransactions;
    }
    return [];
  };

  const currentTabTransactions = getCurrentTabTransactions();
  
  const sortedTransactions = [...currentTabTransactions].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA; // Most recent first
  });

  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);
  const paginatedTransactions = sortedTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const { data, error } = await getSellerStats();
      if (error) {
        console.error('Failed to load stats:', error);
      } else if (data) {
        setStats({
          totalSales: data.total_sales || 0,
          salesThisMonth: data.sales_this_month || 0,
          salesThisMonthChange: data.sales_change || 0,
          totalEarned: data.total_earned || 0,
          totalEarnedChange: data.earnings_change || 0,
        });
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const balanceAmount = balance?.balance || 0;
  // Always use user's selected currency for display
  const displayCurrency = currentCurrency;

  return (
    <div className="wallet-container">
      <div className="wallet-header">
        <div className="wallet-title">
          <Wallet size={24} />
          <h2>{t('wallet.title')}</h2>
        </div>
      </div>

      {isSeller && (
        <div className="wallet-stats-section">
          <div className="wallet-stat-card">
            <div className="wallet-stat-label">Total Sales</div>
            {statsLoading ? (
              <Skeleton width="100px" height="24px" />
            ) : (
              <div className="wallet-stat-value">{stats.totalSales.toLocaleString()}</div>
            )}
          </div>
          <div className="wallet-stat-card">
            <div className="wallet-stat-label">Sales This Month</div>
            {statsLoading ? (
              <Skeleton width="100px" height="24px" />
            ) : (
              <div className="wallet-stat-value">
                {stats.salesThisMonth.toLocaleString()}
                {stats.salesThisMonthChange !== 0 && (
                  <span className={`wallet-stat-trend ${stats.salesThisMonthChange >= 0 ? 'positive' : 'negative'}`}>
                    {stats.salesThisMonthChange >= 0 ? '+' : ''}{stats.salesThisMonthChange}%
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="wallet-stat-card">
            <div className="wallet-stat-label">Total Earned</div>
            {statsLoading ? (
              <Skeleton width="100px" height="24px" />
            ) : (
              <div className="wallet-stat-value">
                {formatCurrency(stats.totalEarned, currentCurrency)}
                {stats.totalEarnedChange !== 0 && (
                  <span className={`wallet-stat-trend ${stats.totalEarnedChange >= 0 ? 'positive' : 'negative'}`}>
                    {stats.totalEarnedChange >= 0 ? '+' : ''}{stats.totalEarnedChange}%
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="wallet-balance-section">
        <div className="wallet-balance-label">{t('wallet.balance')}</div>
        {loading && !balance ? (
          <Skeleton width="150px" height="32px" />
        ) : (
          <div className="wallet-balance-amount">{formatCurrency(balanceAmount)}</div>
        )}
        <div className="wallet-withdrawal-note">
          {t('wallet.withdrawalNote', { amount: `${getCurrencySymbol()}10` })}{' '}
          <button
            type="button"
            className="wallet-learn-more"
            onClick={() => navigate('/help')}
          >
            {t('wallet.learnMore')}
          </button>
        </div>
        
        {isSeller && (
          <div className="wallet-connect-note">
            <p className="wallet-connect-note-title">Stripe Connect Required for Withdrawals</p>
            <p className="wallet-connect-note-copy">
              To withdraw funds, you need to connect your Stripe account. Set up your Stripe Connect account to enable withdrawals.
            </p>
            <button
              type="button"
              className="wallet-connect-btn"
              onClick={() => navigate('/seller/dashboard?tab=payments')}
            >
              Set Up Stripe Connect
              <ExternalLink size={14} />
            </button>
          </div>
        )}
        
        <button
          type="button"
          className="wallet-withdraw-btn"
          onClick={() => setShowWithdrawalModal(true)}
          disabled={parseFloat(balanceAmount || 0) < 10}
        >
          Withdraw
        </button>
        {parseFloat(balanceAmount || 0) < 10 && (
          <p className="wallet-min-note">
            Minimum withdrawal: {getCurrencySymbol()}10
          </p>
        )}
      </div>

      <WithdrawalModal
        isOpen={showWithdrawalModal}
        onClose={() => setShowWithdrawalModal(false)}
        balance={balanceAmount}
        currency={displayCurrency}
        onSuccess={() => {
          clearBalanceCache();
          loadWalletData(false);
        }}
      />

      <div className="wallet-tabs" role="tablist">
        {isSeller && (
          <button
            type="button"
            role="tab"
            className={`wallet-tab${activeTab === 'withdrawals' ? ' is-on' : ''}`}
            onClick={() => {
              setActiveTab('withdrawals');
              setCurrentPage(1);
            }}
          >
            Withdrawal History
          </button>
        )}
        <button
          type="button"
          role="tab"
          className={`wallet-tab${activeTab === 'disputes' ? ' is-on' : ''}`}
          onClick={() => {
            setActiveTab('disputes');
            setCurrentPage(1);
          }}
        >
          Dispute History
        </button>
        <button
          type="button"
          role="tab"
          className={`wallet-tab${activeTab === 'purchases' ? ' is-on' : ''}`}
          onClick={() => {
            setActiveTab('purchases');
            setCurrentPage(1);
          }}
        >
          Purchase History
        </button>
      </div>

      <div className="wallet-filters">
        <div className="wallet-filter-dropdown">
          <button className="wallet-filter-btn">
            {dateFilter === 'all' ? t('wallet.allTime') : dateFilter}
            <ChevronDown size={16} />
          </button>
          <div className="wallet-filter-menu">
            <button onClick={() => setDateFilter('all')}>{t('wallet.allTime')}</button>
            <button onClick={() => setDateFilter('This month')}>{t('wallet.thisMonth')}</button>
            <button onClick={() => setDateFilter('Last month')}>{t('wallet.lastMonth')}</button>
            <button onClick={() => setDateFilter('Last 3 months')}>{t('wallet.last3Months')}</button>
          </div>
        </div>
        <button 
          className="wallet-export-btn"
          onClick={() => {
            // Clear cache and force refresh
            if (typeof localStorage !== 'undefined') {
              localStorage.removeItem('cached_balance');
            }
            loadWalletData(false);
          }}
          title="Refresh withdrawal status"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
        <button className="wallet-export-btn">
          <Download size={16} />
          {t('wallet.export')}
        </button>
      </div>

      {loading && transactions.length === 0 ? (
        <div className="wallet-table-wrapper">
          <table className="wallet-table">
            <thead>
              <tr>
                <th>{t('wallet.dateCreated')}</th>
                <th>{t('wallet.balanceChange')}</th>
                <th>{t('wallet.orderId')}</th>
                <th>{t('wallet.description')}</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => (
                <tr key={i} className="wallet-transaction-row">
                  <td><Skeleton width="180px" height="20px" /></td>
                  <td><Skeleton width="100px" height="20px" /></td>
                  <td><Skeleton width="200px" height="20px" /></td>
                  <td><Skeleton width="150px" height="20px" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : paginatedTransactions.length === 0 ? (
        <div className="wallet-empty">
          <p>
            {activeTab === 'withdrawals' && 'No withdrawal history'}
            {activeTab === 'disputes' && 'No dispute history'}
            {activeTab === 'purchases' && 'No purchase history'}
          </p>
        </div>
      ) : (
        <>
          <div className="wallet-table-wrapper">
            <table className="wallet-table">
              <thead>
                <tr>
                  <th>Date Created</th>
                  <th>Balance change</th>
                  <th>Order ID</th>
                  <th>{t('common.status', 'Status')}</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.map((transaction) => {
                  const rawAmount = parseFloat(transaction.amount || 0);
                  const orderId = getOrderId(transaction);
                  
                  // Determine if transaction is positive (credit) or negative (debit)
                  // Withdrawals and purchases are debits (negative), sales and refunds are credits (positive)
                  const isWithdrawal = transaction.reference_type === 'withdrawal' || transaction.type === 'withdrawal';
                  const isPurchase = transaction.type === 'debit' && transaction.reference_type === 'order';
                  
                  // For dispute history tab:
                  // - If it's a dispute refund (refund_buyer), it's positive
                  // - If it's a dispute loss (pay_seller), it's negative (already set in API)
                  // - If dispute is pending, show original purchase as negative
                  // For dispute tab, use the amount as-is (API handles the sign)
                  // For other tabs, use the standard logic
                  let isPositive;
                  let displayAmount;
                  
                  if (activeTab === 'disputes') {
                    // For disputes, use the amount as-is (API already sets the correct sign)
                    displayAmount = Math.abs(rawAmount);
                    isPositive = rawAmount > 0;
                  } else {
                    // For other tabs, use standard logic
                    displayAmount = Math.abs(rawAmount);
                    isPositive = !isWithdrawal && !isPurchase;
                  }
                  
                  return (
                    <tr key={transaction.id} className="wallet-transaction-row">
                      <td>{formatDate(transaction.created_at || transaction.dispute_info?.created_at)}</td>
                      <td className={isPositive ? 'balance-positive' : 'balance-negative'}>
                        {isPositive ? '+' : '-'}{formatCurrency(displayAmount)}
                      </td>
                      <td className="wallet-order-id">{orderId}</td>
                      <td className="wallet-order-id">
                        {(transaction.status || 'completed').toString()}
                      </td>
                      <td>
                        <div className="wallet-description-cell">
                          <span>{getTransactionDescription(transaction)}</span>
                          <div className="wallet-actions">
                            {transaction.reference_type === 'order' && (
                              <button 
                                className="wallet-action-link"
                                onClick={() =>
                                  navigate(
                                    transaction.metadata?.boosting_request_id
                                      ? `/boosting/requests/${transaction.metadata.boosting_request_id}`
                                      : `/orders/${orderId}`
                                  )
                                }
                              >
                                {t('common.view')}
                              </button>
                            )}
                            {hasReceipt(transaction) && (
                              <button className="wallet-action-link">
                                {t('wallet.viewReceipt', 'View Receipt')}
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="wallet-pagination">
              <button 
                onClick={() => setCurrentPage(1)} 
                disabled={currentPage === 1}
                className="wallet-pagination-btn"
              >
                {t('common.first', 'First')}
              </button>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                disabled={currentPage === 1}
                className="wallet-pagination-btn"
              >
                <ChevronLeft size={16} />
              </button>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                disabled={currentPage === totalPages}
                className="wallet-pagination-btn"
              >
                <ChevronRight size={16} />
              </button>
              <button 
                onClick={() => setCurrentPage(totalPages)} 
                disabled={currentPage === totalPages}
                className="wallet-pagination-btn"
              >
                {t('common.last', 'Last')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WalletTab;

